/**
 * Sidebar — wires:
 *  - active-dot tracking via IntersectionObserver
 *  - dice button → emits 'randomize' event on document
 *
 * Visual color reactivity is automatic via var(--accent).
 */
export class Sidebar {
  constructor(root = document.getElementById('sidebar')) {
    this.root = root;
    if (!root) return;

    this.dots = Array.from(root.querySelectorAll('.sidebar__dot'));
    this.dice = root.querySelector('#dice');

    this._bindDots();
    this._bindDice();
    this._bindMenu();
  }

  _bindDots() {
    // Only intra-page anchors are valid IO targets — world pages use
    // cross-page hrefs ("/" / "/#worlds") that aren't valid CSS selectors.
    // Filter to "#id" form (no slashes, no empty), skip the rest.
    const isLocalAnchor = (href) =>
      typeof href === 'string' && /^#[\w-]+$/.test(href);

    const sections = this.dots
      .map((d) => d.getAttribute('href'))
      .filter(isLocalAnchor)
      .map((href) => document.querySelector(href))
      .filter(Boolean);
    if (!sections.length) return;

    // Click: smooth-scroll with Lenis instead of the browser's jump. The
    // sign-off dot goes to the very bottom (end of the Off Air runway).
    this.dots.forEach((d) => {
      const href = d.getAttribute('href');
      if (!isLocalAnchor(href)) return;
      d.addEventListener('click', (e) => {
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        const toBottom = href === '#offair';
        const y = toBottom ? document.documentElement.scrollHeight - window.innerHeight : target;
        if (window.lenis?.scrollTo) window.lenis.scrollTo(y, { duration: 1.2, offset: toBottom ? 0 : -4 });
        else if (toBottom) window.scrollTo({ top: y, behavior: 'smooth' });
        else target.scrollIntoView({ behavior: 'smooth' });
        history.replaceState(null, '', href);
      });
    });

    // Active dot by scroll position: the last section whose top has passed
    // the viewport's midpoint. (An IntersectionObserver can't handle the Off
    // Air runway — at the page bottom only its tail is in view.)
    const pairs = this.dots
      .map((d) => ({ d, el: isLocalAnchor(d.getAttribute('href')) ? document.querySelector(d.getAttribute('href')) : null }))
      .filter((x) => x.el);
    const update = () => {
      const y = window.scrollY + window.innerHeight * 0.5;
      const atBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      let active = pairs[0];
      for (const x of pairs) if (x.el.offsetTop <= y) active = x;
      if (atBottom) active = pairs[pairs.length - 1];
      pairs.forEach((x) => x.d.classList.toggle('is-active', x === active));
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    if (window.lenis?.on) window.lenis.on('scroll', update);
    update();
  }

  _bindMenu() {
    const btn = this.root.querySelector('#menu-toggle');
    const menu = document.getElementById('site-menu');
    if (!btn || !menu) return;
    const set = (open) => {
      menu.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('is-menu-open', open);
      if (open) { window.lenis?.stop?.(); menu.querySelector('a')?.focus(); }
      else window.lenis?.start?.();
    };
    btn.addEventListener('click', () => set(menu.hidden));
    menu.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      if (a.hasAttribute('data-menu-close')) {
        e.preventDefault();
        set(false);
        const world = a.getAttribute('data-open-world');
        if (world) {
          // Open the world exactly as tapping its card would (grow + channel wipe)
          const card = document.querySelector(`.card[data-world="${world}"]`);
          if (card) { setTimeout(() => card.click(), 120); return; }
        }
        const target = document.querySelector(a.getAttribute('href'));
        if (target) (window.lenis?.scrollTo ? window.lenis.scrollTo(target, { offset: -8 }) : target.scrollIntoView({ behavior: 'smooth' }));
      } else {
        set(false);
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) set(false); });
    this._setMenu = set;
  }

  _bindDice() {
    if (!this.dice) return;
    this.dice.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('randomize'));
      // Until Randomizer lands, do a quick visual ping so the button feels alive:
      this.dice.animate(
        [
          { transform: 'rotate(0deg) scale(1)' },
          { transform: 'rotate(-90deg) scale(1.05)' },
          { transform: 'rotate(0deg) scale(1)' },
        ],
        { duration: 420, easing: 'cubic-bezier(.7,0,.3,1)' }
      );
    });
  }
}
